/** Loud banner when the residential proxy (Decodo) path is down (Tom 2026-08-14:
 * "add a loud banner on the Scrapers page when the proxy path is down, so this
 * surfaces immediately instead of looking like empty data").
 *
 * Every paid fetch — county appraisers, city permit portals, the realtor.com
 * render — rides the Decodo residential proxy. When it's unreachable, all of
 * them return EMPTY, which reads as "this property has no records" rather than
 * "our proxy is down." fetch_backend posts a scraper_health incident the moment
 * a fetch fails (source 'fetch_backend', where 'gate.decodo.com…'), and marks
 * it resolved on the next success — so an unresolved proxy incident in the last
 * couple of hours means the path is down RIGHT NOW. We surface exactly that. */
import { useCallback, useEffect, useState } from "react";
import type { ScraperIncident } from "./api";

// what a proxy-path failure looks like in scraper_health
const PROXY_RE =
  /decodo|residential proxy|proxy (?:tunnel|unreachable|pool|refused|error)|proxyerror|gate\.decodo|no residential pool/i;
const FRESH_MS = 2 * 60 * 60 * 1000; // only nag on incidents from the last 2h

function isProxyIncident(i: ScraperIncident): boolean {
  const hay = `${i.source} ${i.whereTxt} ${i.whatTxt}`;
  return i.source === "fetch_backend" || PROXY_RE.test(hay);
}

function isProxyDown(i: ScraperIncident): boolean {
  if (i.status === "resolved") return false;
  if (!isProxyIncident(i)) return false;
  const t = Date.parse(i.createdAt.endsWith("Z") ? i.createdAt : i.createdAt + "Z");
  // Undatable incidents are STALE, not fresh (2026-08-14: a dateless row kept
  // the banner up forever while the live probe was returning 200).
  return Number.isFinite(t) && Date.now() - t < FRESH_MS;
}

function ago(iso: string): string {
  const t = Date.parse(iso.endsWith("Z") ? iso : iso + "Z");
  if (!Number.isFinite(t)) return "";
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.round(m / 60)}h ago`;
}

export function ProxyStatusBanner({ loadHealth }: { loadHealth: () => Promise<ScraperIncident[]> }) {
  const [down, setDown] = useState<ScraperIncident | null>(null);

  const check = useCallback(async () => {
    try {
      const rows = await loadHealth();
      const hit = rows.filter(isProxyDown)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] || null;
      // A RESOLVED/ok proxy row NEWER than the failure means the path came
      // back — don't nag on the older incident (2026-08-14: banner showed
      // DOWN while the live probe was 200).
      const okAfter = hit && rows.some((r) => isProxyIncident(r)
        && r.status === "resolved" && r.createdAt > hit.createdAt);
      setDown(okAfter ? null : hit);
    } catch { /* transient; leave last state */ }
  }, [loadHealth]);

  useEffect(() => {
    void check();
    const t = setInterval(() => void check(), 60000); // re-check each minute
    return () => clearInterval(t);
  }, [check]);

  if (!down) return null;

  return (
    <div className="proxydown" role="alert" style={{
      background: "#7f1d1d", color: "#fff", borderRadius: 10, padding: "12px 16px",
      margin: "0 0 12px", boxShadow: "0 1px 0 rgba(0,0,0,.15)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: 15 }}>
        <span aria-hidden style={{ fontSize: 18 }}>⛔</span>
        Residential proxy is DOWN — paid scraping is failing right now
      </div>
      <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.5, opacity: 0.95 }}>
        County appraisers, city permit portals and the realtor.com permit render all go through
        the Decodo residential proxy. While it's down they return <b>empty</b> — so blank county /
        permit / owner data is the proxy, not the property. First seen {ago(down.createdAt)}.
        {down.whatTxt ? <> <span style={{ opacity: 0.85 }}>({down.whatTxt.slice(0, 160)})</span></> : null}
      </div>
      <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.5 }}>
        Fix on the Decodo side: confirm the subscription is active, this server's IP is allow-listed,
        and PROXY_USER / PROXY_PASS are still valid. It comes back on its own once the proxy answers —
        this banner clears automatically.
      </div>
    </div>
  );
}
